/**
 * Revision History
 * 
 * 12/13/2021   ptkim       Add getListItems for JobListView's data
 * 12/23/2021   ptkim       Add getJobListPanelItems, getJobAcctDetails
 */
import axios from 'axios';
import _ from 'lodash';
import postal from 'postal';

import {isElementFocusable} from '../util';
import config from '../config';
import {JobNames} from '../framework.core/JobNames';
import {SQLInfo, SQLType} from '../framework.core/data/SQLInfo';
import {ApiResult} from '../framework.core/data/ApiResult';
import {BillingTemplateItem, DbCheckCmd, PayrollSetup} from '../ifmsTypes';
import {HistoryPanelItem} from '../fms.data/models/HistoryPanelItem';
import {MemoPanelItem} from '../fms.data/models/MemoPanelItem';
import {AcctHistoryType} from '../fms.core/AcctHistHd';
import { FmsAutoNumberType, ListFilterType, SQLIndex} from '../fms.core/constants';
import {DepoHd} from '../fms.core/DepoHd';
import {DocumentType} from "../framework.core/DocumentType";
import { DepositCheckClear } from '../fms.core/DepositCheckClear';
import { CreditCardClear } from '../fms.core/CreditCardClear';
import { CheckLookup } from '../fms.data/lookup/CheckLookup';
import { BlEventLog } from '../fms.core/BlEventLog';
import { GeneralValue } from '../framework.core/data/GeneralValue';
import { EdiSharedInfo } from '../fms.core/Edi310Header';
import { DepoDetail } from '../fms.core/DepoDetail';
import { PimsCustomer } from '../fms.core/pims/PimsCustomer';
import { DisplayDownload } from '../fms.core/BlDownloadItem';

const channel = postal.channel('ajax');

const Reporters = [
  { id: '0', label: 'Test'},
];

const CntrTypes = [
  { value: 'L', label: 'LCL'},
  { value: 'F', label: 'FCL'},
  { value: 'O', label: 'FAK'},
];


type SalesCostParent = JobNames.OIHbl | JobNames.OIMbl |
  JobNames.OEMbl | JobNames.OEHbl |
  JobNames.AIMbl | JobNames.AIHbl |
  JobNames.AEMbl | JobNames.AEHbl;

function markValidationFieldsAsDirty(errors:any) {
  if (!errors || errors.length <= 0) {
    return;
  }

  const dirtyElements:any = [];

  _.forOwn(errors, (_, key) => {
    const dirtyElementsForProperty = document.querySelectorAll(`[data-validationfor="${key}"]`);
    for (let i = 0; i < dirtyElementsForProperty.length; ++i) {
      dirtyElements.push(dirtyElementsForProperty[i]);
    }
  });

  let focusApplied = false;
  for (let i = 0; i < dirtyElements.length; ++i) {
    const element = dirtyElements[i];
    if (!focusApplied) {
      if (isElementFocusable(element)) {
        element.focus();
        focusApplied = true;
      }
    }

    element.classList.add('ifms-validation-error');
  }
}

function formatValidationErrors(errors:any) {
  const errorHtml = _.map(errors, value => Array.isArray(value) ? value.join(', ') : value)
    .reduce((acc, x) => `${acc}<li style="white-space: pre-wrap">${x}</li>`, '');

  return `<ul>${errorHtml}</ul>`;
}

async function apiCallResolver(apiCall:any, silent:boolean=false, loadingMsg: boolean=false) {
  let apiResult = null;

  if (!silent) {
    channel.publish('ajax.start', {});
  }
  if (loadingMsg) {
    channel.publish('ajax.loadingstart', {});
  }

  try
  {
    const response = await apiCall();

    if (response.config.method === 'post' || response.config.method === 'put') {
      if (!silent) {
        channel.publish('ajax.save', 'Data have been saved');
      }
    }

    if (response.config.method === 'delete') {
      if (!silent) {
        channel.publish('ajax.delete', 'Data have been deleted');
      }
    }

    apiResult = {
      success: true,
      data: response.data
    };
  }
  catch(e:any) {
    let data = undefined;
    if (e.response && e.response.data) {
      data = e.response.data;
    }

    apiResult = {
      success: false,
      data
    };

    if (e.response) {
      const responseStatus = e.response.status;

      let errorTitle = 'Error';
      let errorText:any = '';
      let errorPayload = null;
      let errorType = 'error';

      switch (responseStatus) {
        case 400:
          markValidationFieldsAsDirty(apiResult?.data?.errors || []);

          errorTitle = 'Data validation error';
          errorText = apiResult && apiResult.data && apiResult.data.errors ? formatValidationErrors(apiResult.data.errors) : null;
          errorPayload = apiResult.data;
          break;
        case 401:
        case 403:
          errorTitle = 'You are not authorized for this operation';
          break;
        case 424:
          errorTitle = 'Warning';
          errorType = 'warning';
          errorText = apiResult && apiResult.data && apiResult.data.errors ? formatValidationErrors(apiResult.data.errors) : null;
          break;
        default:
          errorTitle = 'Internal Server Error';
      }
      if (!silent) {
        channel.publish('ajax.error', {errorTitle, errorPayload, errorText, errorType});
      }
      //<240221.dwshin>
      if (process.env.NODE_ENV === 'production' && e.response.data?.errors === 'SessionId') {
        e.response.data.errors = 'Please refresh the page and try again.';
        channel.publish('ajax.movetologin', {});
      }
    }
  }

  if (!silent) {
    channel.publish('ajax.end', {});
  }
  if (loadingMsg) {
    channel.publish('ajax.loadingend', {});
  }

  return apiResult;
}


export interface IGetFieldValuePara {
  id: string, 
  tableName:string,
  fieldName:string,
  idFieldName?:string, 
  fieldName2?:string,
}

function createIfmsApi() {
  const apiClient = axios.create();

  const apiUrl = config.serverUrl;

  return {
    setAccessToken: (accessToken:any) => {
      const headers = _.cloneDeep(apiClient.defaults.headers);
      headers.common.Authorization = `Bearer ${accessToken}`;

      apiClient.defaults.headers = headers;
    },

    //#region - users
    login: async (username:any, password:any, urlPrefix:string) => {
      const apiResult = await apiCallResolver(() => apiClient.post(
        `${apiUrl}user/login/`,
        {
          email: username,
          password,
          urlPrefix
        },
      ));

      return {
        success: apiResult.success,
        user: apiResult.data,
        errorMessage: !apiResult.success ? 'User not found' : null,
      }
    },

    
    autologin: async (info:string) => {
      const apiResult = await apiCallResolver(() => apiClient.post(
        `${apiUrl}user/autologin/`,
        {
          info
        },
      ));

      return apiResult.success ? apiResult.data : {};
    },


    //#endregion - users

    authenticate: async (parms: any) => {
      const apiResult = await apiCallResolver(() => apiClient.post(
        `${apiUrl}system/authenticate/`,
         parms
      ));
      
      return apiResult.success ? apiResult.data : {};
    },
    
    authenticateLogoff: async () => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}system/authenticatelogoff/`,
      ));
      
      return apiResult.success ? apiResult.data : {};
    },

    //#region - system
    getSystemSettings: async () => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}system/settings`,
      ));

      return apiResult.success ? apiResult.data : {};
    },

    /**
     * When error, error:error-msg
     * otherwise, next it number
     */
    getNextItNo: async (branch:string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}system/nextItNo`,
        {
          params: { branch }
        }
      ));

      return apiResult.success ? apiResult.data : null;
    },

    //#endregion - system

    //#region - {jobName}
    /**
     * Commom JobDeail get.
     * @param jobName {jobNames}
     * @param c { id: , ...}
     * @returns ApiResult<T>
     */
    getJobDetail: async (jobName:string, c: any) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}${jobName}/detail`,
        { params: c}
      ), true, true);

      return apiResult.success ? apiResult.data : null;
    },


    /**
     * Save with SQLInfo array
     * @param jobName 
     * @param sqlList 
     * @param params {} job related props to return
     * @returns ApiResult<InsertResultWithData>
     */
    saveJobDetail: async (jobName:JobNames, sqlList: SQLInfo[], params:any) => {
      const apiResult = await apiCallResolver(() => apiClient.put(
        `${apiUrl}${jobName}/save`,
        sqlList,
        { params }
      ));
    
      return apiResult.success ? apiResult.data : null;
    },

    /**
 * Save with SQLInfo array
 * @param jobName 
 * @param sqlList 
 * @param params {} job related props to return
 * @returns ApiResult<InsertResultWithData>
 */
    saveUpload: async (jobName: JobNames, sqlList: SQLInfo[]) => {
      const apiResult = await apiCallResolver(() => apiClient.put(
        `${apiUrl}${jobName}/saveupload`,
        sqlList,
      ));

      return apiResult.success ? apiResult.data : null;
    },

    /**
     * Delete jobDetail and Pims Info if exists
     * @param jobName 
     * @param c { id: , ...}
     * @returns ApiResult
     */
     deleteJobDetail: async (jobName:JobNames, c: any) => {
      const apiResult = await apiCallResolver(() => apiClient.delete(
        `${apiUrl}${jobName}`,
        {
          params: c
        }
      ));

      return apiResult.success ? apiResult.data : null;
    },

    /**
     * Called JobDetailView component before calling save to call check logic
     *  for the job which need access to database.
     * @param jobName 
     * @param jobId 
     * @param j {object}  dynamic request params
     * @returns {json string}  For each property of j, "" or "error"
     */
     checkData: async(jobName:string, jobId:string, j:any) => {
      const apiResult = await apiCallResolver(() => apiClient.post(
        `${apiUrl}${jobName}/checkdata`,
        j,
        {
          params: { jobId }
        }
      ));
      return apiResult.success ? apiResult.data : {};
    },
    checkDupData: async(jobName:string, jobId:string, j:any) => {
      const apiResult = await apiCallResolver(() => apiClient.post(
        `${apiUrl}${jobName}/checkdupNo`,
        j,
        {
          params: { jobId }
        }
      ));
      return apiResult.success ? apiResult.data : {};
    },
    checkAgentRefData: async(jobName:string, jobId:string, j:any) => {
      const apiResult = await apiCallResolver(() => apiClient.post(
        `${apiUrl}${jobName}/checkagentRefNo`,
        j,
        {
          params: { jobId }
        }
      ));
      return apiResult.success ? apiResult.data : {};
    },
    checkCancel: async(jobName:string, centerNo:string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}${jobName}/checkcancel`,
        {
          params: { centerNo }
        }
      ));
      return apiResult.success ? apiResult.data : {};
    },
    getAccountCodeInfo:async (jobName:string, id:number, billTo:number, currency:string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}${jobName}/getAccountCodeInfo`,
        {
          params: { id, billTo, currency }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },
    /**
     * Get { tbName, tbId} for JobNames.Invoice, JobNames.Bill
     * @param jobName 
     * @param id 
     */
     getSourceJobInfo: async(jobName:string, id: any) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}${jobName}/getsourcejobinfo?id=${id}`
      ));
      return apiResult.success ? apiResult.data : null;
    },
    
    getMblData:async (jobName:string, id:string, readCont:boolean) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}${jobName}/getmbldata`,
        {
          params: { id, readContainer: readCont }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getAdjBalance: async(jobName:string, postDate: string, id: number, hdId: number, isEdit: boolean, currency: string|'') => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}${jobName}/getAdjBalance`,
        { 
          params: { postDate, id, hdId, isEdit, currency }
        }
      ));
    
      return apiResult.success ? apiResult.data : null;
    },
    
    //<pt220928: add customerId(agent), currency, shipmentType as optional for crdb>
    getBillingTplInfoList: async (jobName: string, srcJobName: string, srcJobId: number, 
      customerId:number = 0, currency:string = 'USD', shipmentType:string = 'I') => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}${jobName}/getBillingTplInfoList`,
        {
          params: {srcJobName, srcJobId, customerId, currency, shipmentType}
        }
      ));
      return apiResult.success ? apiResult.data : [];
    },
    getAutoBillingInfoList: async (jobName: string, srcJobName: string, customerId:number = 0, shipmentType:string = 'I') => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}${jobName}/getAutoBillingInfoList`,
        {
          params: {srcJobName, customerId, shipmentType}
        }
      ));
      return apiResult.success ? apiResult.data : [];
    },
    getBillInformationData: async (id: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}${JobNames.Bill}/getBillInformationData`,
        {
          params: { id }
        }
      ));
      return apiResult.success ? apiResult.data : [];
    },
    saveBillInformationData: async (sqlList: SQLInfo[]) => {
      const apiResult = await apiCallResolver(() => apiClient.post(
        `${apiUrl}${JobNames.Bill}/saveBillInformationData`,
        sqlList
      ));
      return apiResult.success ? apiResult.data : null;
    },
    getLoadBilling: async (jobName: string, fromDate: string, toDate: string, comId: number) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}${jobName}/getLoadBilling`,
        {
          params: {fromDate, toDate, comId}
        }
      ));
      return apiResult.success ? apiResult.data : [];
    },
    isCancelGiveCredit: async(jobName:string, id:number, invDate:string, currency:string, billToId:number) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}${jobName}/isCancelGiveCredit`,
        { params: { id, invDate, currency, billToId }}
      ));
      return apiResult.success ? apiResult.data : null;
    },

    taxReturnCheck: async(jobName:string, jobId:string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}${jobName}/taxReturnCheck`,
        {
          params: { id: jobId }
        }
      ));
      return apiResult.success ? apiResult.data : {};
    },
    
    //#endregion - {jobName}

    //#region - bills

    setBillConfirmed: async (id: number, confirmed: boolean) => {
      const apiResult = await apiCallResolver(() => apiClient.put(
        `${apiUrl}${JobNames.Bill}/confirmed`,
        null,
        {
          params: {
            id: id,
            confirmed: confirmed
          }
        }
      ));
      return apiResult.success ? apiResult.data : [];
    },




    saveOrUpdateBill: async (c:any) => {
      const apiResult = await apiCallResolver(() => apiClient.put(
        `${apiUrl}bills/save`,
        c,
      ));
    
      return apiResult.success ? apiResult.data : null;
    },
    getBill: async (parent: SalesCostParent, parentId: number, id: number|undefined) => {
      const apiRoute = !! id ? `${apiUrl}bills/${parent}/${parentId}/${id}` : `${apiUrl}bills/${parent}/${parentId}`;

      const apiResult = await apiCallResolver(() => apiClient.get(
        apiRoute,
      ), true, true);

      return apiResult.success ? apiResult.data : null;
    },
    creditBill: async (c:any) => {
      const apiResult = await apiCallResolver(() => apiClient.put(
        `${apiUrl}bills/credit`,
        c,
      ));
    
      return apiResult.success ? apiResult.data : null;
    },
    voidBill: async (id:any) => {
      const apiResult = await apiCallResolver(() => apiClient.put(
        `${apiUrl}bills/void/${id}`,
      ));
    
      return apiResult.success ? apiResult.data : null;
    },
    //#endregion - bills

    //#region - invoices
    saveOrUpdateInvoice: async (c:any) => {
      const apiResult = await apiCallResolver(() => apiClient.put(
        `${apiUrl}invoices/save`,
        c,
      ));
    
      return apiResult.success ? apiResult.data : null;
    },
    getInvoice: async (parent: SalesCostParent, parentId: number, id: number|undefined) => {
      const apiRoute = !! id ? `${apiUrl}invoices/${parent}/${parentId}/${id}` : `${apiUrl}invoices/${parent}/${parentId}`;

      const apiResult = await apiCallResolver(() => apiClient.get(
        apiRoute,
      ), true, true);

      return apiResult.success ? apiResult.data : null;
    },
    creditInvoice: async (c:any) => {
      const apiResult = await apiCallResolver(() => apiClient.put(
        `${apiUrl}invoices/credit`,
        c,
      ));
    
      return apiResult.success ? apiResult.data : null;
    },
    voidInvoice: async (id:any) => {
      const apiResult = await apiCallResolver(() => apiClient.put(
        `${apiUrl}invoices/void/${id}`,
      ));
    
      return apiResult.success ? apiResult.data : null;
    },
    setInvoiceConfirmed: async (id: number, confirmed: boolean) => {
      const apiResult = await apiCallResolver(() => apiClient.put(
        `${apiUrl}${JobNames.Invoice}/confirmed`,
        null,
        {
          params: {
            id: id,
            confirmed: confirmed
          }
        }
      ));
      return apiResult.success ? apiResult.data : [];
    },

    //#endregion - invoices

    //#region - crdbs
    saveOrUpdateCrdb: async (c:any) => {
      const apiResult = await apiCallResolver(() => apiClient.put(
        `${apiUrl}crdbs/save`,
        c,
      ));
    
      return apiResult.success ? apiResult.data : null;
    },
    setCrdbConfirmed: async (id: number, confirmed: boolean) => {
      const apiResult = await apiCallResolver(() => apiClient.put(
        `${apiUrl}CrDb/confirmed`,
        null,
        {
          params: {
            id: id,
            confirmed: confirmed
          }
        }
      ));
      return apiResult.success ? apiResult.data : [];
    },

    saveRCrDbData: async (jobId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.put(
        `${apiUrl}CrDb/saveRCrDbData`,
        null,
        {
          params: {
            jobId: jobId,
          }
        }
      ));
      return apiResult.success ? apiResult.data : [];
    },

    getCrdb: async (parent: SalesCostParent, parentId: number, id: number|undefined, agentCur: string|undefined) => {
      const apiRoute = !! id ? `${apiUrl}crdbs/${parent}/${parentId}/${id}` : `${apiUrl}crdbs/${parent}/${parentId}`;

      const apiResult = await apiCallResolver(() => apiClient.get(
        apiRoute,
        {
          params: {
            agentCur,
          }
        },
      ), true, true);

      return apiResult.success ? apiResult.data : null;
    },
    voidCrdb: async (id:any) => {
      const apiResult = await apiCallResolver(() => apiClient.put(
        `${apiUrl}crdbs/void/${id}`,
      ));
    
      return apiResult.success ? apiResult.data : null;
    },

    //#endregion - crdbs

    //#region - oemb
    cmdCopyBodyQuery1: async (id: number) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}oemb/cmdCopyBodyQuery1`,
        {params:{id}}
      ));
    
      return apiResult.success ? apiResult.data : null;
    },
    
    cmdCopyBodyQuery2: async (id: number) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}oemb/cmdCopyBodyQuery2`,
        {params:{id}}
      ));
    
      return apiResult.success ? apiResult.data : null;
    },
    //#endregion - oemb

    //#region - oimb
    canOceanImportMblContainerBeDeleted: async (containerId:any) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}oimb/canDeleteContainer`,
        {params:{id: containerId}}
      ));

      return apiResult.success ? apiResult.data : false;
    },

    //#endregion - oimb

    //#region - bill
    getOceanImportBillNoExists: async (billId:any, billNo:any, vendorId:any) => {
      const id = !!billId ? billId : 0;
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}bill/billNoExists`,
        {
          params: {
            id,
            billNo,
            vendorId
          }
        },
      ));

      return apiResult.success ? apiResult.data : false;
    },
    /**
     * 
     * @param billId 
     * @param billDate 
     * @param cur 
     * @param vendorId 
     * @returns 
     */
     isCancelReceiveCredit: async(billId:number, billDate:string|null, cur:string, vendorId:number) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}bill/isCancelReceiveCredit`,
        { params: { billId, billDate, cur, vendorId }}
      ));
      return apiResult.success ? apiResult.data : null;
    },

    //#endregion - bill

    //#region - quotations

    /**
     * use QuotationLookup, obsolete lookup.
     * @param customer 
     * @returns 
     */
    lookupOiQuotations: async (customer:any) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}quotations/lookup/${customer}`,
      ));

      return apiResult.success ? apiResult.data : null;
    },
    //#endregion - quotations

    //#region - pecd
    closeAccount: async(sDate:string, sLoginID:string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}pecd/closeAccount`,
        { params: { sDate, sLoginID } }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    closeOperation: async(sDate:string, sLoginID:string, byAcct:boolean, bOp:boolean, bUseTrans:boolean) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}pecd/closeOperation`,
        { params: { sDate, sLoginID, byAcct, bOp, bUseTrans } }
      ));
      return apiResult.success ? apiResult.data : null;
    },
    //#endregion - pecd
    
    //#region - modlist

    /**
     * pt211223
     * Get MemoPanelItems, HistoryPanelItems for the given jobName/jobId
     * @param jobName 
     * @param jobId 
     * @param com1Id {number} only for oih/aih (CustomerId), oeh/aeh (Agent)
     * @param com2Id {number} only for oeh/aeh (Customer)
     * @returns 
     */
    getJobListPanelItems: async(jobName:string, jobId:number, com1Id?:number, com2Id?:number): Promise<{
      jobName:string, jobId:number, memo:MemoPanelItem[], //updateHistory:MemoPanelItem[], 
      logHistory:HistoryPanelItem[]
    }> => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}modlist/getjoblistpanelitems`,
        {
          params: { jobName, jobId, com1Id, com2Id }
        }
      ));

      return apiResult.success ? JSON.parse((apiResult.data as ApiResult<string>).result) : {};
    },
  
    /**
     * pt211223
     * Get AcctDetail[] for InfoArea
     * @param jobName 
     * @param jobId 
     * @returns {AcctDetail[]}
     */
     getJobAcctDetails: async (jobName:string, jobId:number) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}modlist/getjobacctdetails`,
        { params: { jobName, jobId }}
      ));
      
      return apiResult.success ? apiResult.data.result : null;
    },

    getAcctHistory: async (jobName: JobNames, jobId: number, jobType: string, isDelete: boolean, fromDate?: string, toDate?: string) => {
      let histType = '';
      if(jobName === JobNames.Invoice || jobName === JobNames.GInvo || jobName === JobNames.WHStorageInv || jobName === JobNames.WHInvoice) histType = isDelete ? AcctHistoryType.DInvoHistory : AcctHistoryType.InvoHistory;
      else if(jobName === JobNames.Bill || jobName === JobNames.GBill) histType = isDelete ? AcctHistoryType.DApHistory : AcctHistoryType.ApHistory;
      else if(jobName === JobNames.CrDb) histType = isDelete ? AcctHistoryType.DCrDbHistory : AcctHistoryType.CrDbHistory;
      else return null;   // invalid jobname

      if (jobType === '') jobType = 'noType'; //If jobType is null, api does not execute

      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}modlist/getaccthistory/${histType}/${jobType}/${jobId}`,
        {
          params: { fromDate, toDate }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getPaymentHistoryForAcct: async (tbId: string, tbName: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}modlist/getPaymentHistoryForAcct`,
        { params: { tbId, tbName }}
      ));
      
      return apiResult.success ? apiResult.data : null;
    },

    getDeleteHistory: async (tbName: string, fromDate?: string, toDate?: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}modlist/getDeleteHistory`,
        { params: { tbName, fromDate, toDate }}
      ));
      
      return apiResult.success ? apiResult.data : null;
    },

    getPaymentHistory: async (jobId: string, jobType: string) => {
        const apiResult = await apiCallResolver(() => apiClient.get(
            `${apiUrl}modlist/getPaymentHistory`,
            { params: { jobId, jobType } }
        ));

        return apiResult.success ? apiResult.data : null;
    },

    getDeletePaymentHistory: async (jobType: string, fromDate: string, toDate: string) => {
        const apiResult = await apiCallResolver(() => apiClient.get(
            `${apiUrl}modlist/getDeletePaymentHistory`,
            { params: { jobType, fromDate, toDate } }
        ));

        return apiResult.success ? apiResult.data : null;
    },

    getTaskReminder: async (showAll: boolean, userId : string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}modlist/getTaskReminder`,
        { params: { showAll, userId }}
      ));
      
      return apiResult.success ? apiResult.data : null;
    },
    /**
     * [POST] Get list items of the given jobName satisfying search condition.
     * @param jobName framework.core/JobNames
     * @param listItemType Class name of list item in fms.data/models/???ListItem.ts
     * @param search Instance of list item class which value of property to search or not
     * @param start Next index of list item to collect
     * @param limit The max number of list items to collect
     * @param other Other parameters if required
     * @returns framework/core/data/ListResult
     */
    getListItems: async(jobName: string, listItemType: string, search: any, start: number, limit: number, other: any, listFilterType:ListFilterType) => {
      // Special handling required job
      switch(jobName) {
        case JobNames.Invoice:
          if(search.extraWhere === JobNames.OceanImport) search.extraWhere = "jobtype in ('oih', 'oim')";
          else if(search.extraWhere === JobNames.OceanExport) search.extraWhere = "jobtype in ('ooh', 'oom')";
          else if(search.extraWhere === JobNames.AirImport) search.extraWhere = "jobtype in ('aih', 'aim')";
          else if(search.extraWhere === JobNames.AirExport) search.extraWhere = "jobtype in ('aoh', 'aom')";
          else if(search.extraWhere === JobNames.OtherOP) search.extraWhere = "jobtype in ('oth')";
          else if(search.extraWhere === JobNames.Warehouse) search.extraWhere = "jobtype in ('whr', 'whs')";
          break;

        case JobNames.Bill:
          if(search.extraWhere === JobNames.OceanImport) search.extraWhere = "jobtype in ('oih', 'oim')";
          else if(search.extraWhere === JobNames.OceanExport) search.extraWhere = "jobtype in ('ooh', 'oom')";
          else if(search.extraWhere === JobNames.AirImport) search.extraWhere = "jobtype in ('aih', 'aim')";
          else if(search.extraWhere === JobNames.AirExport) search.extraWhere = "jobtype in ('aoh', 'aom')";
          else if(search.extraWhere === JobNames.OtherOP) search.extraWhere = "jobtype in ('oth')";
          else if(search.extraWhere === JobNames.Warehouse) search.extraWhere = "jobtype in ('whr', 'whs')";
          break;

        case JobNames.CrDb:
          if(search.extraWhere === JobNames.OceanImport) search.extraWhere = "jobtype in ('oih', 'oim')";
          else if(search.extraWhere === JobNames.OceanExport) search.extraWhere = "jobtype in ('ooh', 'oom')";
          else if(search.extraWhere === JobNames.AirImport) search.extraWhere = "jobtype in ('aih', 'aim')";
          else if(search.extraWhere === JobNames.AirExport) search.extraWhere = "jobtype in ('aoh', 'aom')";
          else if(search.extraWhere === JobNames.OtherOP) search.extraWhere = "jobtype in ('oth')";
          else if(search.extraWhere === JobNames.Warehouse) search.extraWhere = "jobtype in ('whr', 'whs')";
          break;
      }
      
      const apiResult = await apiCallResolver(() => apiClient.post(
        `${apiUrl}modlist/getlistitems`,
        {
          jobName,
          listItemType,
          start,
          limit,
          searchModel: search,
          filterType: listFilterType,
          other: typeof other === 'string' ? other : undefined
        }
      ), true, true);
      return apiResult.success ? apiResult.data : [];      
    },    

    /**
     * Get ListResult like getListItems for lookupIndex type
     * return: ListResult | null
     */
     getLookupItems: async(lookupIndex: SQLIndex, searchText: string, formPara: string | '', formPara2: string | '', formPara3:string | '', maxRecords:number | 100) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}modlist/getlookupitems`,
        {
          params: { lookupIndex, searchText, formPara, formPara2, formPara3, maxRecords }   
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },
    
    //<pt220813: portal
    saveListItem: async (listItemType:string, sqlType:SQLType, entity:any) => {
      const apiResult = await apiCallResolver(() => apiClient.post(
        `${apiUrl}modlist/savelistitem`,
        { listItemType, sqlType, entity }
      ));
      return apiResult.success ? apiResult.data : null;
    },
    //pt220813>

    saveList: async (listItemType:string, entity:any) => {
      const apiResult = await apiCallResolver(() => apiClient.post(
        `${apiUrl}modlist/saveList`,
        { listItemType, entity }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    //#endregion - modlist

    //#region - mbls

    /**
     * use InfoArea. obsolete
     * @param id 
     * @returns 
     */
    getOceanImportMblInfoAreaBics: async (id:any) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}mbls/infoAreaBics/${id}`,
      ));

      return apiResult.success ? apiResult.data : null;
    },
   
    //#endregion - mbls

    //#region - subscriptionId

    getSettingsSubscriberIdList: async () => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}subscriptionId/list`,
        {
        }
      ));

      return apiResult.success ? apiResult.data : [];
    },
    changeSettingsSubscriberId: async (c:any) => {
      const apiResult = await apiCallResolver(() => apiClient.put(
        `${apiUrl}subscriptionId/change`,
        null,
        { 
          params:c
        }
        //c,
      ));

      return apiResult.success ? apiResult.data : [];
    },
    //#endregion - subscriptionId

    getCntrTypes: () => new Promise(resolve => {
      resolve(CntrTypes);
    }),
    getReporters: () => new Promise(resolve => {
      resolve(Reporters);
    }),

    //#region - test
    // For testing - should be removed
    getTestDates: async () => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}test/date`,
      ));

      return apiResult.success ? apiResult.data : [];
    },
    formatDate: async (dateTimeParam:string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}test/formatDate`,
        {
          params: {
            dateTimeParam,
          }
        }
      ));

      return apiResult.success ? apiResult.data : [];
    },
    //#endregion - test

    //#region - blstatus

    /**
     * Get fms.core.HblStatusViewModel for container milestone
     * @param jobName {string} HBL jobName
     * @param jobId 
     * @returns {ApiResult<HblStatusViewModel>}
     */
     getHblStatusViewModel: async (jobName:string, jobId:number) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}blstatus/gethblstatus/${jobName}/${jobId}`
      ));
      return apiResult.success ? apiResult.data : null;
    },

    /**
     * Save changes of HBL container milestones
     * @param sqlList 
     * @returns {ApiResult}
     */
    saveHblStatusViewModel: async (sqlList:SQLInfo[]) => {
      const apiResult = await apiCallResolver(() => apiClient.post(
        `${apiUrl}blstatus/savehblstatus`,
        sqlList
      ));
      return apiResult.success ? apiResult.data : null;
    },

    /**
     * 220721.dwshin. unused?
     * Get fms.core.MblStatusViewModel for container milestone
     * @param jobName {string} MBL jobName
     * @param jobId 
     * @returns {ApiResult<MblStatusViewModel>}
     */
    getMblStatusViewModel: async (jobName:string, jobId:number) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}blstatus/getmblstatus/${jobName}/${jobId}`
      ));
      return apiResult.success ? apiResult.data : null;
    },

    /**
     * Save changes of MBL container milestone
     * @param sqlList 
     * @returns {ApiResult}
     */
    saveMblStatusViewModel: async(sqlList:SQLInfo[]) => {
      const apiResult = await apiCallResolver(() => apiClient.post(
        `${apiUrl}blstatus/savemblstatus`,
        sqlList
      ));
      return apiResult.success ? apiResult.data : null;
    },

    updateContainerInfo: async(data:SQLInfo) => {
      const apiResult = await apiCallResolver(() => apiClient.post(
        `${apiUrl}blstatus/updateContainerInfo`,
        data
      ));
      return apiResult.success ? apiResult.data : null;
    },

    updateContainerInfos: async(data:SQLInfo[]) => {
      const apiResult = await apiCallResolver(() => apiClient.post(
        `${apiUrl}blstatus/updateContainerInfos`,
        data
      ));
      return apiResult.success ? apiResult.data : null;
    },

    updateANSentInfo: async(hblId: number, sentDate: string|null) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}blstatus/updateANSentInfo`,
        { 
          params: { 
            hblId, sentDate 
          }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    /**
     * [uni-model] Unified version to get profit view model
     * @param jobName 
     * @param jobId 
     * @returns {ApiResult<ProfitResult[]>}
     */
    getProfitResults: async (jobName:string, jobId:number) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}blstatus/getprofitresults/${jobName}/${jobId}`,
      ));

      return apiResult.success ? apiResult.data : null;
    },

    getJobSalesCost: async(jobName:string, id:number) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}blstatus/getjobsalescost/${jobName}/${id}`
      ));

      return apiResult.success ? apiResult.data : null;
    },

    //#endregion - blstatus

    //#region - fmsmodfunc

    /**
     * 
     * @param c { id, tableName, fieldName, idFieldName?, fieldName2?}
     * @returns 
     */
     getFieldValue: async(c:IGetFieldValuePara) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}fmsmodfunc/getfieldvalue`,
        {
          params: { ...c }
        }
      ));
      return apiResult.success ? apiResult.data : '';
    },
    
    /**
     * 
     * @param query
     * @returns 
     */
    getFieldValueQuery: async(query: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}fmsmodfunc/getFieldValueQuery`,
        {
          params: { query }
        }
      ));
      return apiResult.success ? apiResult.data : '';
    },

    /**
     * 
     * @param c { id, tableName, fieldName, idFieldName?, fieldName2?}
     * @returns 
     */
     getFieldValueInt: async(c:IGetFieldValuePara) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}fmsmodfunc/getfieldvalueint`,
        {
          params: { ...c }
        }
      ));
      return apiResult.success ? apiResult.data : 0;
    },

    /**
     * 
     * @param c { id, tableName, fieldName, idFieldName?, fieldName2?}
     * @returns 
     */
    getFieldValueDate: async(c:IGetFieldValuePara) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}fmsmodfunc/getfieldvaluedate`,
        {
          params: { ...c }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getCompanyNameFromCompanyFile: async(id: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}fmsModFunc/getCompanyNameFromCompanyFile`,
        {
          params:{ id }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getCheckFormName: async(jobId: number) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}fmsModFunc/getCheckFormName`,
        {
          params:{ jobId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getAddressFromMyCompany: async(includeCompanyName: boolean = false, includeAddress: boolean= false, includeTEL: boolean= false, includeFAX: boolean = false, 
      includeURL: boolean = false, branch: string = "", extrName: string = "", includeTaxID: boolean = false) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}fmsModFunc/getAddressFromMyCompany`,
        {
          params:{ 
            includeCompanyName, includeAddress, includeTEL, includeFAX, includeURL, branch, extrName, includeTaxID 
          }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getContactFromCompanyFile: async(id: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}fmsModFunc/getContactFromCompanyFile`,
        {
          params:{ id }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    checkAutoNo: async(autoNo: string, tbName: string, fName:string, kValue:string, kFName: string, allowDupInvNo: boolean) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}fmsModFunc/checkAutoNo`,
        {
          params:{ autoNo, tbName, fName, kValue, kFName, allowDupInvNo }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    //<pt220831>
    checkSettingAutoNo: async(autoType:FmsAutoNumberType, branch:string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}fmsModFunc/checkSettingAutoNo`,
        { params: {autoType, branch }}
      ));
      return apiResult.success ? apiResult.data : null;
    },

    /**
     * check if there is any FY which date belongs to.
     * @param date 
     * @returns boolean
     */
     checkupFYDate: async(date: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}fmsmodFunc/checkupFYDate`,
        {
          params: { date }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    /**
     * if no FY exist which date belongs to, then create FY and return true.
     * if FY already exist, then return true.
     * @param date 
     * @returns boolean false only when new FY creation fail.
     */
    checkupFiscalYear: async(date: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}fmsmodfunc/checkupFiscalYear`,
        { params: { date }}
      ));
      return apiResult.success ? apiResult.data : null;
    },
    opClose: async(jobName:string, keyValue:string, isOpClose:boolean) => {
      const apiResult = await apiCallResolver(() => apiClient.post(
        `${apiUrl}fmsModFunc/opclose`,
        {
          jobName, keyValue, isOpClose  
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },
    
    /**
     * Used to validate Air Port Code. If valid, the Port Name is reutrn
     * @param portCode 
     * @returns 
     */
     getCityCountryByPortCode: async(portCode: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}fmsModFunc/GetCityCountryByPortCode`,
        {
          params: { portCode }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getFieldValueFromInitData: async(sFieldName: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}fmsmodfunc/getFieldValueFromInitData`,
        {
          params: { sFieldName }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    /**
     * ocean port validation
     * @param portCode un code
     * @returns string[] - [0]: port name, [1]: us code
     */
     getUNCodeInfo: async(portCode: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}fmsModFunc/getUNCodeInfo`,
        {
          params: { portCode }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    /**
     * getCountCreditData
     * @param stbName 
     * @param stbId 
     * @returns ApiResult<number>
     */
     getCountCreditData: async(stbName:string, stbId:number) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}fmsModFunc/getCountCreditData`,
        { params: { stbName, stbId }}
      ));
      return apiResult.success ? apiResult.data : null;
    },
    
    existCreditItem: async (jobId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}fmsModFunc/existCreditItem`,
        {
          params: { jobId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    invoiceOkDate: async(vDate:string|null) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}fmsmodfunc/invoiceOkDate`,
        { params: { vDate } }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    checkupDuplicateFieldValueIsDuplicate: async(tableName: string, fieldName: string, fieldValue: string, keyFieldName: string, exceptKeyValue: number) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}fmsmodfunc/checkupDuplicateFieldValueIsDuplicate`,
        { params: { tableName, fieldName, fieldValue, keyFieldName, exceptKeyValue }}
      ));
      return apiResult.success ? apiResult.data : null;
    },

    /**
     * 
     * @param blName 
     * @param blId 
     * @param lineAgents 
     * @param tbName 
     * @param tbId 
     * @returns ApiResult<Array<number>>|null
     */
    isUseOperationAgent: async(blName:string, blId:number, lineAgents:string, tbName:string, tbId:number) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}fmsmodfunc/isUseOperationAgent`,
        { params: { blName, blId, lineAgents, tbName, tbId }}
      ));
      return apiResult.success ? apiResult.data : null;
    },

    /**
     * 
     * @param tbId 
     * @param tbName 
     * @param blId 
     * @param blName 
     * @param dcLine 
     * @param daLine 
     * @param readLevel 
     * @param prevDBValue 
     * @returns ApiResult<number>|null
     */
     dutyEntityExist: async(tbId:number, tbName:string, blId:number, blName:string, dcLine:boolean, daLine:boolean,
      readLevel:number = 0, prevDBValue:number = 0) => {
        const apiResult = await apiCallResolver(() => apiClient.get(
          `${apiUrl}fmsmodfunc/dutyEntityExist`,
          { params: { tbId, tbName, blId, blName, dcLine, daLine, readLevel, prevDBValue}}
        ));
        return apiResult.success ? apiResult.data : null;
    },

    closeCheckAccount: async(sDate:string, sLoginID:string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}fmsmodfunc/closeCheckAccount`,
        { params: { sDate, sLoginID } }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    CheckCloseDateNew: async (jobName: string, postDate: string, fileClosed: number, billReceived: boolean = false) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}fmsmodfunc/CheckCloseDateNew`,
        { params: { jobName, postDate, fileClosed, billReceived} }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    CheckCloseDate: async (jobName: string, sId: string, errMsgType: number = 1, useToday: boolean = false) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}fmsmodfunc/CheckCloseDate`,
        { params: { jobName, sId, errMsgType, useToday } }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    checkFinalizeData: async(tbName:string, tbId:string, silent: boolean = false) => {
        const apiResult = await apiCallResolver(() => apiClient.get(
          `${apiUrl}fmsmodfunc/checkFinalizeData`,
          { params: { tbName, tbId }}
        ), silent, !silent);
        return apiResult.success ? apiResult.data : null;
    },

    getEstFinalizeDate: async(tbName:string, tbId:string, silent: boolean = false) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}fmsmodfunc/getEstFinalizeDate`,
        { params: { tbName, tbId }}
      ), silent, !silent);
      return apiResult.success ? apiResult.data : null;
    },

    getEstFinalizeDateEx: async(tbName:string, tbId:string, silent: boolean = false) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}fmsmodfunc/getEstFinalizeDateEx`,
        { params: { tbName, tbId }}
      ), silent, !silent);
      return apiResult.success ? apiResult.data : null;
    },

    setRecognizeBLForm: async(jobName:string, jobId:number, flag:boolean) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}fmsmodfunc/setRecognizeBLForm`,
        { params: { jobName, jobId, flag }}
      ));
      return apiResult.success ? apiResult.data : null;
    },

    setOIetaByVessel: async(newEta:string|null, vessel:string, voyage:string, discharge:string, oldEta:string|null, setReminder:boolean) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}fmsmodfunc/setOIetaByVessel`,
        { params: { newEta, vessel, voyage, discharge, oldEta, setReminder }}
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getBillingCodeValue: async(billingCode:string, billingDept:string, billingType: number) => {
      const apiResult = await apiCallResolver(()=> apiClient.get(
        `${apiUrl}fmsmodfunc/getBillingCodeValue`,
        { params: {billingCode, billingDept, billingType}}
      ));
      return apiResult.success ? apiResult.data: null;
    },
    
    getESupportURL: async(acmeId:string) => {
      const apiResult = await apiCallResolver(()=> apiClient.get(
        `${apiUrl}fmsmodfunc/getESupportURL`,
        { params: {acmeId}}
      ));
      return apiResult.success ? apiResult.data: null;
    },

    //#region ClsAddressText > SetDataFromCompanyFile

    setDataFromCompanyFile: async(companyId: number) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}fmsmodfunc/setDataFromCompanyFile`,
        { params: { companyId }}
      ));
      return apiResult.success ? apiResult.data : null;
    },

    //#endregion

    companyFlagCheck: async (id: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}fmsmodfunc/companyFlagCheck`,
        {
          params: { id }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    //#endregion - fmsmodfunc

    //#region - modfsnewfunc
    checkCompanyLookupField: async(lkComs: CheckLookup[]) => {
      let msg = "";
      if(lkComs.length > 0) {   
        const apiResult = await apiCallResolver(() => apiClient.post(
          `${apiUrl}modfsnewfunc/checkcompanylookupfield`,
          lkComs
        ));
        
        msg = apiResult.success ? apiResult.data : "";
      }
      return msg;
    },
    
    getBeginDateFY: async(date: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}modfsnewfunc/getBeginDateFY`,
        {
          params: { date }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getCloseDate: async() => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}modfsnewfunc/getCloseDate`, { }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getNextSlipSeqNo: async(postDate: string, branch: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}modfsnewfunc/getNextSlipSeqNo`,
        {
          params: { postDate, branch }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    //#endregion - modfsnewfunc    
    //pt211226>

    //#region - modfunc

    /**
     * Return true for valid number, otherwise return false.
     * @param amblNo MAWB No
     * @param carrierId Ai(Ae)Mbl.Carrier
     */
    checkAirMblNo: async (amblNo:string, carrierId:number) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}modfunc/checkairmblno/${carrierId}`,
        {
          params: { amblNo }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getAddressFromCompanyFile: async(id: string, bIncludeCompanyName: boolean = false, bIncludeContactName: boolean = false, 
      bInclueTEL: boolean = false, bIncludeFAX: boolean = false, bIncludeEmail: boolean = false,
      bIncludeCountry: boolean = false, bDeliveryAddress: boolean = false, bIncludeTaxID: boolean = false) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}modfunc/getAddressFromCompanyFile`,
        {
          params:{ 
            id, 
            bIncludeCompanyName, 
            bIncludeContactName, 
            bInclueTEL, 
            bIncludeFAX, 
            bIncludeEmail, 
            bIncludeCountry, 
            bDeliveryAddress, 
            bIncludeTaxID
          }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getCustomerInfo: async(id: string, customer: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}invo/getCustomerInfo`,
        {
          params:{id}
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getInvoiceCount: async(id: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}invo/getInvoiceCount`,
        {
          params:{ id }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getRList: async(tbId: string, tbName: string, fieldNames: string, idFieldName: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}modfunc/getRList`,
        {
          params:{ tbId, tbName, fieldNames, idFieldName }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getAutoCheckNo: async(bankId: string, branch: string | '') => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}modfunc/getAutoCheckNo`,
        {
          params:{ bankId, branch}
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },
    
    checkItNo: async (inBondNo: string, entryCheckNumber: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}modfunc/checkitno`,
        {
          params: { inBondNo, entryCheckNumber }
        }
      ));
      return apiResult.success ? apiResult.data : {};
    },

    //#endregion - modfunc

    //#region - modsub
    /**
     * Load items for combobox
     * @param comboParams 
     * @returns Array of { comboType, id, defaultIndex, items} where items = textValue[] or string[] or object[] according to FmsComboType
     */
    loadCodeData: async (cparams: any[]) => {
      const apiResult = await apiCallResolver(() => apiClient.post(
        `${apiUrl}modsub/loadCodeData`,
        cparams
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getTrackRequestedContainerCountPerMbl: async (oimblId: number) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}modsub/getTrackRequestedContainerCountPerMbl`,
        {
          params: { oimblId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },
    //#endregion - modsub

    //#region - salescost
    getJobSalesCostPendingItems: async(jobName:string, id:number) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}salesCost/pendingItems`,
        {
          params: { jobName, id }   
        }
      ));

      return apiResult.success ? apiResult.data : null;
    },

    getJobSalesCostSummary: async(jobName:string, id:number) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}salesCost/summary`, { params: { jobName, id}}
      ));

      return apiResult.success ? apiResult.data : null;
    },

    getBicBillingTemplate: async(templatePrefix: 'AIMIS' | 'AIMDS' | 'AIHIS' | 'AIHDS' | 'AEMIS' | 'AEMDS' | 'AEHIS' | 'AEHDS' |
      'OIMIS' | 'OIMDS' | 'OIHIS' | 'OIHDS' | 'OEMIS' | 'OEMDS' | 'OEHIS' | 'OEHDS' | 'OTHIS' | 'OTHDS' | 'WHR' | 'WHS'): Promise<Array<BillingTemplateItem>> => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}salesCost/billingTemplate/${templatePrefix}`
      ), true);

      return apiResult.success ? apiResult.data : null;
    },

    getUsedAgents: async(jobName:string, id:number) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}salesCost/getUsedAgents`,
        {
          params: { jobName, id }   
        }
      ));

      return apiResult.success ? apiResult.data : null;
    },
    //#endregion - salescost

    //#region - cpay

    readAccountData: async(depoHd:DepoHd) => {
      const apiResult = await apiCallResolver(() => apiClient.post(
        `${apiUrl}cpay/readaccountdata`,
        {depoHd}
      ), false, true);
      return apiResult.success ? apiResult.data : {};
    },

    getPendingARAP: async(type: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}cpay/getPendingARAP`,
        {
          params: { type }
        }
      ), false, true);
      return apiResult.success ? apiResult.data : {};
    },
    
    getRate: async (accrualDate:string, bankId:number, bankCur:string, billTo:number, id:number, paymentType:string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}cpay/getRate`,
        {
          params: { accrualDate, bankId, bankCur, billTo, id, paymentType }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getAccumulatedBalance: async (jobId:number, date:string, originDate:string, toDate:string, 
      paidAmt:number, accountCode:string, currency:string, entityId:number, paymentType:string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}cpay/getAccumulatedBalance`,
        {
          params: { jobId, date, originDate, toDate, paidAmt, accountCode, currency, entityId, paymentType }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    updatePayment: async (sqlList: SQLInfo[]) => {
      const apiResult = await apiCallResolver(() => apiClient.put(
        `${apiUrl}cpay/updatePayment`,
        sqlList
      ));
      return apiResult.success ? apiResult.data : [];
    },

    getPaymentDetail: async (jobId: number, paymentType: string, isCanUseHybrid: boolean = false) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}cpay/getPaymentDetail`,
        {
          params: { jobId, paymentType, isCanUseHybrid }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    setVoidFieldByDepoData: async (jobId: number, actionType: number, inputDate: string, defaultDate: string, u2Date: string, isDeposit: boolean, isVoid: boolean, isJournal: boolean, 
      type: string, invIds: string, billIds: string, crdbIds: string, taxRtnIds: string, payrollIds: string, ccPayIds: string, acctList: DepoDetail[]) => {
      const apiResult = await apiCallResolver(() => apiClient.put(
        `${apiUrl}cpay/setVoidFieldByDepoData`,
        acctList,
        {
          params: { jobId, actionType, inputDate, defaultDate, u2Date, isDeposit, isVoid, isJournal, type, invIds, billIds, crdbIds, taxRtnIds, payrollIds, ccPayIds }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },
    //#endregion - cpay


    //#region - auto Billing

    getAutoNo: async(eNoType: string, sBranch: string, sTableID: string, sType:object) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}fmsModFunc/getAutoNo`,
        {
          params:{ eNoType, sBranch, sTableID, sType }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    abinvDetailSelect:async (year:number, month:number, group:string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}${JobNames.AutoBillingInvoice}/abinvDetailSelect`,
        {
          params: { year, month, group }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },



    saveBillingDetailList: async (sqlList: SQLInfo[]) => {
      const apiResult = await apiCallResolver(() => apiClient.put(
        `${apiUrl}${JobNames.AutoBillingInvoice}/saveBillingDetailList`,
        sqlList,
      ));
    
      return apiResult.success ? apiResult.data : null;
    },



    selectAotherTemp:async (ids:string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}${JobNames.AutoBillingInvoice}/selectAotherTemp`,
        {
          params: { ids }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

     //#endregion - auto Billing


    //#region - reportoptionbase
    getCargoTypeVolume: async (selectedYear: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/getCargoTypeVolume`,
        {
          params: { selectedYear }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getCargoTypeProfit: async (selectedYear: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/getCargoTypeProfit`,
        {
          params: { selectedYear }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    GetEstProfit5Years: async (selectedYear: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/GetEstProfit5Years`,
        {
          params: { selectedYear }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },


    getGrossProfit5Years: async (selectedYear: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/getGrossProfit5Years`,
        {
          params: { selectedYear }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getTop15SalesData: async (customerType: string, asOfDate: string, q1StartDate: string, q1EndDate: string, q2StartDate: string, q2EndDate: string, q3StartDate: string, q3EndDate: string, q4StartDate: string, q4EndDate: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/getTop15SalesData`,
        {
          params: { customerType, asOfDate, q1StartDate, q1EndDate, q2StartDate, q2EndDate, q3StartDate, q3EndDate, q4StartDate, q4EndDate }
        }
      ), false, true);
      return apiResult.success ? apiResult.data : null;
    },

    getAccountBalance: async (toDate: string, comId: number, comType: string, branch: string, subCode: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/getAccountBalance`,
        {
          params: { toDate, comId, comType, branch, subCode }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getVolumebyCargoTypeYears: async (jobName: string, unit: string, toDate: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/getVolumebyCargoTypeYears`,
        {
          params: { jobName, unit, toDate }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    loadHouseList: async(tbName: string, tbId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/loadHouseList`,
        {
          params:{ tbName, tbId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },
    loadBatchHouseList: async(tbName: string, tbId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/loadBatchHouseList`,
        {
          params:{ tbName, tbId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getMasterArrivalNoticeRemark: async(tbName: string, tbId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/getMasterArrivalNoticeRemark`,
        {
          params:{ tbName, tbId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    readArrivalNoticeInvoiceList: async(id: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/readArrivalNoticeInvoiceList`,
        {
          params:{ id }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getDicPPCCbyCustomer: async(custIds: any[]) => {
      const apiResult = await apiCallResolver(() => apiClient.post(
        `${apiUrl}reportOptionBase/getDicPPCCbyCustomer`,
        custIds
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getArrivalNoticeConfirm: async(tbName: string, tbId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/getArrivalNoticeConfirm`,
        {
          params:{ tbName, tbId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    saveArrivalNoticePublicMemo: async(tbName: string, tbId: string, pMemo: string) => {
      await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/saveArrivalNoticePublicMemo`,
        {
          data:{ tbName, tbId, pMemo }
        }
      ));
    },

    readPickupDeliveryInstructionData: async(tbId: string, tbName: string, docType: DocumentType) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/readPickupDeliveryInstructionData`,
        {
          params:{ tbId, tbName, docType }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    readPickupHistoryList: async(tbName: string, tbId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/readPickupHistoryList`,
        {
          params:{ tbName, tbId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    readMultiDeliveryList: async(custId: string, delName: string, delAddr: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/readMultiDeliveryList`,
        {
          params:{ custId, delName, delAddr }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    readOtherOiHblNoList: async(mblId: number, custId: number, hblId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/readOtherOiHblNoList`,
        {
          params:{ mblId, custId, hblId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    readAwbViewData: async(tbName: string, tbId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/readAwbViewData`,
        {
          params:{ tbName, tbId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    readAwbViewDataEx: async(tbName: string, tbId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/readAwbViewDataEx`,
        {
          params:{ tbName, tbId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    saveAwbViewData: async(tbName: string, tbId: string, sigShipper: string|'', sigCarrier: string|'', firstCarrier: string|'', destCountry: string|'') => {
      await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/saveAwbViewData`,
        {
          params:{ tbName, tbId, sigShipper, sigCarrier, firstCarrier, destCountry }
        }
      ));
    },

    readTrasferManifestViewData: async(tbId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/readTrasferManifestViewData`,
        {
          params:{ tbId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },
    saveTransferManifestData: async(tbId: string, cutoff: string | undefined, remark: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/saveTransferManifestData`,
        {
          params: { tbId, cutoff, remark }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    readWhLabelViewData: async(tbId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/readWhLabelViewData`,
        {
          params:{ tbId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },
    
    readAirLabelData: async(docType: DocumentType, tbName: string, tbId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/readAirLabelData`,
        {
          params:{ docType, tbName, tbId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    readArrivalReleaseNoticeDate: async(tbId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/readArrivalReleaseNoticeDate`,
        {
          params:{ tbId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    readDeliveryOrderShortFormData: async(tbName: string, tbId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/readDeliveryOrderShortFormData`,
        {
          params:{ tbName, tbId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    readEmptyContainerReturnGuaranteeData: async(tbId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/readEmptyContainerReturnGuaranteeData`,
        {
          params:{ tbId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    readOemBatchPrintData: async(tbId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/readOemBatchPrintData`,
        {
          params:{ tbId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    readCancelNoticeData: async(tbId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/readCancelNoticeData`,
        {
          params:{ tbId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    readMemoListData: async(tbName: string, tbId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/readMemoListData`,
        {
          params:{ tbName, tbId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    readPreliminaryClaimNoticeData: async(tbName: string, tbId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/readPreliminaryClaimNoticeData`,
        {
          params:{ tbName, tbId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    readMblArriavalNoticeData: async(tbName: string, tbId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/readMblArrivalNoticeData`,
        {
          params:{ tbName, tbId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    
    readMblHoldNoticeData: async(tbId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/readMblHoldNoticeData`,
        {
          params:{ tbId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    readPackageLabelViewData: async(tbName: string, tbId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/readPackageLabelViewData`,
        {
          params:{ tbName, tbId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    readProofOfDeliveryData: async(tbName: string, tbId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/readProofOfDeliveryData`,
        {
          params:{ tbName, tbId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    readReleaseOrderTurnoverData: async(tbName: string, tbId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/readReleaseOrderTurnoverData`,
        {
          params:{ tbName, tbId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    saveReleaseOrderTurnoverData: async(tbId: string, date: string, by: string, hblNo: string) => {
      await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/saveReleaseOrderTurnoverData`,
        {
          params:{ tbId, date, by, hblNo }
        }
      ));
    },

    readItTeViewData: async(tbName: string, tbId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/readItTeViewData`,
        {
          params:{ tbName, tbId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    readStraightBLViewData: async(tbName: string, tbId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/readStraightBLViewData`,
        {
          params:{ tbName, tbId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    readPrintOeHblData: async(tbName: string, tbId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/readPrintOeHblData`,
        {
          params:{ tbName, tbId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    saveOeHblViewData: async(tbId: string, optTitle: string, firstCarrier: string, seller: string, consignee: string, buyer: string) => {
      await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/saveOeHblViewData`,
        {
          params:{ tbId, optTitle, firstCarrier, seller, consignee, buyer }
        }
      ));
    },

    readDocumentPackageData: async(tbName: string, tbId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/readDocumentPackageData`,
        {
          params:{ tbName, tbId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    readAgnetDocData: async(tbName: string, tbId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/readAgentDocViewData`,
        {
          params:{tbName, tbId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    readCargoMViewData: async(tbName: string, tbId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/readCargoMViewData`,
        {
          params:{tbName, tbId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    readShippingAViewData: async(tbName: string, tbId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/readShippingAViewData`,
        {
          params:{tbName, tbId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    readBatchPViewData: async(tbId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/readBatchPViewData`,
        {
          params:{tbId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },
    readBankDraftData: async(tbId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/readBankDraftData`,
        {
          params:{ tbId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    readBankDraftBLData: async(tbName: string, tbId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/readBankDraftBLData`,
        {
          params:{ tbName, tbId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },
    readDeliveryOrderCompanyList: async(tbId: string, priName: string, priDelAddr: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/readDeliveryOrderCompanyList`,
        {
          params:{ tbId, priName, priDelAddr }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },
    
    readArrivalNoticeHBLData: async(tbName: string, tbId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/readArrivalNoticeHBLData`,
        {
          params:{ tbName, tbId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getArrivalNoticeLogHistory: async(tbName: string, tbId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/getArrivalNoticeLogHistory`,
        {
          params:{ tbName, tbId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getFYPeriodInfo: async(date: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/getFYPeriodInfo`,
        {
          params: { date }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },
    getLoadHouseAgentList: async(tbName: string, tbId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/getLoadHouseAgentList`,
        {
          params: { tbName, tbId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },
    getLoadContactUserList: async() => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/getLoadContactUserList`,
      ));
      return apiResult.success ? apiResult.data : null;
    },

    readMemoListModel: async(tbId: string, tbName: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/readMemoListModel`,
        {
          params: { tbId, tbName }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    readBatchPrintList: async(docType: DocumentType, rangeType: string | undefined, fromDate: string | undefined, toDate: string | undefined, fromNumber: number|undefined, toNumber: number|undefined,  branch: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/readBatchPrintList`,
        {
          params: { docType, rangeType, fromDate, toDate, fromNo: fromNumber, toNo: toNumber, branch }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },
    
    loadApList: async(fromDate: string | '', toDate: string | '', checkNo: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/loadApList`,
        {
          params: { fromDate, toDate, checkNo }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    readCargoManifestSQ: async(jobName: string, jobId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/readCargoManifestSQ`,
        {
          params: { jobName, jobId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    queryCargoStatus: async(args: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/queryCargoStatus`,
        {
          params: { args }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },
    
    issueCheck: async(data: any) => {
      const apiResult = await apiCallResolver(() => apiClient.post(
        `${apiUrl}reportOptionBase/issueCheck`,
        data
      ));
      return apiResult.success ? apiResult.data : null;
    },

    savePickupDeliveryInstructionData: async(data: any, pimsLinkId: number) => {
      const apiResult = await apiCallResolver(() => apiClient.post(
        `${apiUrl}reportOptionBase/savePickupDeliveryInstructionData`,
        {data, pimsLinkId}
      ));
      return apiResult.success ? apiResult.data : null;
    },

    saveBankDraftData: async (sqlList: SQLInfo[]) => {
      const apiResult = await apiCallResolver(() => apiClient.put(
        `${apiUrl}reportOptionBase/saveBankDraftData`,
        sqlList,
      ));
    
      return apiResult.success ? apiResult.data : null;
    },

    loadBLData: async(recordCnt: string, id: number, oi: boolean, oo: boolean, ai: boolean, ao: boolean, ot: boolean, bt:boolean) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/loadBLData`,
        { 
          params: { recordCnt, id, oi, oo, ai, ao, ot, bt } 
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    readBaseInfoCompany: async(id: number) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/readBaseInfoCompany`,
        { 
          params: { id } 
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    readBaseInfoHistory: async(id: number) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/readBaseInfoHistory`,
        { 
          params: { id } 
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    readCompanyAddressLabelList: async (group: string, searchText: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/readCompanyAddressLabelList`,
        {
          params: { group, searchText }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getAbiRspDataByReqId: async(abiReqId: number) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/getAbiRspDataByReqId`,
        { 
          params: { abiReqId } 
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    queryImporterBond: async(args: any) => {
      const apiResult = await apiCallResolver(() => apiClient.post(
        `${apiUrl}reportOptionBase/queryImporterBond`,
        args
      ));
      return apiResult.success ? apiResult.data : null;
    },

     readPouchAirLabelData: async (id: number) => {
          const apiResult = await apiCallResolver(() => apiClient.get(
              `${apiUrl}reportOptionBase/readPouchAirLabelData`,
              {
                  params: { id }
              }
          ));
          return apiResult.success ? apiResult.data : null;
     },
    //#endregion - reportoptionbase

    //#region - oebk
    getQuoteData:async (id:string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}oebk/getquotedata`,
        {
          params: { id }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getAutoHblNo:async (id:string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}oebk/getautohblno`,
        {
          params: { id }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },
    //#endregion - oebk

    //#region - whrc
    getOnHandList:async (id:string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}whrc/getonhandlist`,
        {
          params: { id }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },
    //#endregion - whrc

    //#region - whLoc
    getItemCount: async (id:string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}whloc/getitemcount`,
        { params: {id} }
      ));
      return apiResult.success ? apiResult.data : null;
    },
    //#endregion - whLoc

    //#region - fmsmodaccount
    getLastFiscalDate: async(date: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}fmsModAccount/getLastFiscalDate`,
        {
          params: { date }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    /**
     * called invoiceview, billview, crdbview
     * @param ids comma separated id's
     * @returns Array<Partial<OpAcct>>
     */
     getOpAccts: async (ids:string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}fmsmodaccount/getOpAccts`,
        { params: { ids }}
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getCountFYPeriod: async (fromDate: string, toDate: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}fmsModAccount/getCountFYPeriod`,
        { 
          params: { fromDate, toDate }
        }
      ));
      return apiResult.success ? apiResult.data: null;
    },
    //#endregion - fmsmodaccount

    //#region - fmsmoddbfunc
    getBrokerEmailFax: async(aOType: string, id: number, withSep: boolean) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}fmsModDbFunc/getBrokerEmailFax`,
        {
          params:{ aOType, id, withSep }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    checkSamePeriod: async(date: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}fmsModDbFunc/checkSamePeriod`,
        {
          params: { date }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },
    //#endregion - fmsmoddbfunc

    //#region - settings
    dbCheck: (cmd: DbCheckCmd, onMessage: (message: string) => void, onComplete: Function) => {
      const urlParams = Object.entries(cmd).map(e => e.join('=')).join('&');

      const eventSource = new EventSource(`${apiUrl}settings/dbcheck?${urlParams}&jwt=${apiClient.defaults.headers.common.Authorization}`, { withCredentials: true } );
      eventSource.onmessage = (e) => {
        if (_.startsWith(e.data, '<<end>>')) {
          eventSource.close();
          onComplete(e.data);
          return;
        }
        onMessage(e.data);
      };
    },

    cancelDbCheck: async () => {
      const apiResult = await apiCallResolver(() => apiClient.post(
        `${apiUrl}settings/canceldbcheck`
      ));
      return apiResult.success;
    },

    recreateSetupCode: async(cmd: DbCheckCmd) => {
      const apiResult = await apiCallResolver(() => apiClient.post(
        `${apiUrl}settings/recreatesetupcode`,
        cmd,
      ));
      return apiResult;
    },
    //#endregion - settings

    //#region - bdebt
    getBadARBankInfo: async() => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}bdebt/getBadARBankInfo`
      ));
    
      return apiResult.success ? apiResult.data : null;
    },

    //#endregion - bdebt

    //#region - banktran
    getDepositClearData: async(entity: DepositCheckClear, jobName: string) => {
      const apiResult = await apiCallResolver(() => apiClient.put(
        `${apiUrl}bankTran/getDepositClearData`,
        entity,
        { 
          params:{jobName: jobName} 
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },
    getCheckClearData: async (entity: DepositCheckClear, jobName: string) => {
      const apiResult = await apiCallResolver(() => apiClient.put(
        `${apiUrl}bankTran/getCheckClearData`,
        entity,
        { 
          params:{jobName: jobName} 
        }
      ));
    
      return apiResult.success ? apiResult.data : null;
    },
    getCheckClearExcelData: async (entity: DepositCheckClear) => {
      const apiResult = await apiCallResolver(() => apiClient.put(
        `${apiUrl}bankTran/getCheckClearExcelData`,
        entity
      ));
    
      return apiResult.success ? apiResult.data : null;
    },
    getCreditCardClearData: async(entity: CreditCardClear, jobName: string) => {
      const apiResult = await apiCallResolver(() => apiClient.put(
        `${apiUrl}bankTran/getCreditCardClearData`,
        entity,
        { 
          params:{jobName: jobName} 
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },
    saveDepositCheckCelar: async (sqlList: SQLInfo[]) => {
      const apiResult = await apiCallResolver(() => apiClient.put(
        `${apiUrl}bankTran/saveDepositCheckCelar`,
        sqlList,
      ));
    
      return apiResult.success ? apiResult.data : null;
    },
    depositCheckCelarCheckData: async (entity: DepositCheckClear, jobName: string) => {
      const apiResult = await apiCallResolver(() => apiClient.put(
        `${apiUrl}bankTran/depositCheckCelarCheckData`,
        entity,
        { 
          params:{jobName: jobName} 
        }
      ));
    
      return apiResult.success ? apiResult.data : null;
    },
    creditCardCelarCheckData: async (entity: CreditCardClear, jobName: string) => {
      const apiResult = await apiCallResolver(() => apiClient.put(
        `${apiUrl}bankTran/creditCardCelarCheckData`,
        entity,
        { 
          params:{jobName: jobName} 
        }
      ));
    
      return apiResult.success ? apiResult.data : null;
    },    
    //#endregion - banktran

    //#region - aembl
    getCodesAirlineNextAwbNo: async (id:number) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}${JobNames.AEMbl}/getCodesAirlineNextAwbNo`,
        { params: { id  }}
      ));

      return apiResult.success ? apiResult.data : null;
    },

    fsGetScRate: async (id:string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}${JobNames.AEMbl}/fsGetScRate`,
        { params: { carrierCode: id  }}
      ));

      return apiResult.success ? apiResult.data : null;
    },

    getDimensionInfo: async (id:number) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}${JobNames.AEMbl}/getDimensionInfo`,
        { params: { id  }}
      ));

      return apiResult.success ? apiResult.data : null;
    },
    //#endregion - aembl

    //#region - payrollsetup

    getPayrollSetup: async (): Promise<PayrollSetup|null> => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}payrollSetup`,
        {
        }
      ));

      return apiResult.success ? apiResult.data as PayrollSetup  : null;
    },
    
    
    //#endregion - payrollsetup

    //#region - invo
    getLoadStorage: async (toDate: string, comId: number) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}Invo/getLoadStorage`,
        {
          params: {toDate, comId}
        }
      ));
      return apiResult.success ? apiResult.data : [];
    },

    //#endregion - invo

    getAuthKey:()=>{
      return apiClient.defaults.headers.common.Authorization;
    },

    //#region - fmsmoddbfunc
    getLatestExchangeRate: async(curr:string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}fmsmoddbfunc/getLatestExchangeRate`,
        { params: { curr }}
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getExchangeRate: async(dt:string, curr:string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}fmsmoddbfunc/getExchangeRate`,
        { params: { dt, curr }}
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getOOHBLFormName: async(formTypeIsCopy:boolean) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}fmsmoddbfunc/getOOHBLFormName`,
        { params: { formTypeIsCopy }}
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getBeginningJournal: async() => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}fmsmoddbfunc/getBeginningJournal`,
      ));
      return apiResult.success ? apiResult.data : null;
    },
    //#endregion - fmsmoddbfunc

    //#region - crdb
    crdbSelectDetail: async(srcJobId:number, srcJobName:string, opAgentId:number, currency:string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}crdb/selectDetail`,
        { params: { srcJobId, srcJobName, opAgentId, currency }}
      ));
      return apiResult.success ? apiResult.data : null;
    },
    //#endregion - crdb

    readStorageBillingList: async(billingGroup: string, endDate: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}abstr/readstoragebillinglist`,
        { params: { billingGroup, endDate }}
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getItnNo: async(hblNo: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}oehb/getitnno`,
        { params: { hblNo }}
      ));
      return apiResult.success ? apiResult.data : null;
    },

    upload: async ( formData: FormData) => {
      const apiResult = await apiCallResolver(() => apiClient.post(
        `${apiUrl}memo/upload`,
        formData
      ));
      return apiResult.success ? apiResult.data : null;
    },

    checkfileserver: async () => {
      const apiResult = await apiCallResolver(() => apiClient.post(
        `${apiUrl}memo/checkfileserver`,
      ));
      return apiResult.success ? apiResult.data : null;
    },

    //#region - Batch Recognize
    
    readRecognizeData: async(fromDate: string, toDate: string, searchTbName: string, showNoBilling: boolean) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}brecg/readRecognizeData`,
        { params: { fromDate, toDate, searchTbName, showNoBilling }}
      ));
      return apiResult.success ? apiResult.data : null;
    },

    readRecogHistoryData: async(id: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}brecg/readRecogHistoryData`,
        { params: { id}}
      ));
      return apiResult.success ? apiResult.data : null;
    },

    saveRecognizeData: async (sqlList: SQLInfo[], silent: boolean = false) => {
      const apiResult = await apiCallResolver(() => apiClient.put(
        `${apiUrl}brecg/saveRecognizeData`,
        sqlList,
      ), silent, !silent);
    
      return apiResult.success ? apiResult.data : null;
    },

    saveRecogHistoryData: async (sqlList: SQLInfo[]) =>{
      const apiResult = await apiCallResolver(() => apiClient.put(
        `${apiUrl}brecg/saveRecogHistoryData`,
        sqlList,
      ));
      return apiResult.success ? apiResult.data : null;
    },

    //#endregion - Batch Recognize

    fixJournalByTask: async(tbName: string, jobName: string, jobId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}fmsmodaccount/FixJournalByTask`,
        { params: { tbName, jobName, jobId }}
      ),true,true);
      return apiResult.success ? apiResult.data : null;
    },

    fixJournalByMaster: async(tbName: string, jobName: string, jobId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}fmsmodaccount/FixJournalByMaster`,
        { params: { tbName, jobName, jobId }}
      ),true,true);
      return apiResult.success ? apiResult.data : null;
    },
    
    fixOpAcct : async(tbName: string, tbid: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}fmsmodaccount/UpdatePostedByOpAcct`,
        { params : {tbName, tbid}}
      ), true, true);
        return apiResult.success ? apiResult.data : null;
    },

    checkMissingField : async(id: number, tbName: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}rpaes/checkMissingField`,
        { params : {id, tbName}}
      ));
        return apiResult.success ? apiResult.data : null;
    },

    makeEDIFile : async(id: number, tbName: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}rpaes/makeEDIFile`,
        { params : {id, tbName}}
      ));
        return apiResult.success ? apiResult.data : null;
    },

    makeACEFile : async(id: number, tbName: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}rpaes/makeACEFile`,
        { params : {id, tbName}}
      ));
        return apiResult.success ? apiResult.data : null;
    },

    getSecTasks : async(tbName: string, tbid: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}secgr/getSecTasks`,
      ));
        return apiResult.success ? apiResult.data : null;
    },

    getSecGroups : async() => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}secgr/getSecGroups`,
      ));
        return apiResult.success ? apiResult.data : null;
    },

    getSecurityControlList : async()=>{
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}secgr/getSecurityControlList`,
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getMaxIden : async() => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}secgr/getMaxIden`,
      ));
        return apiResult.success ? apiResult.data : null;
    },

    saveUser : async(sqlList: SQLInfo[]) => {
      const apiResult = await apiCallResolver(() => apiClient.put(
        `${apiUrl}secgr/saveUser`,
        sqlList
      ));
        return apiResult.success ? apiResult.data : null;
    },

    makeCfsManifest: async(tbId: number) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}cfsManifestEdi/makeCfsManifest`,
        {
          params: { tbId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getAmsHbl: async(amsHouseId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}ams/getAmsHbl`,
        {
          params: { amsHouseId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getAmsMbl: async(amsMasterId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}ams/getAmsMbl`,
        {
          params: { amsMasterId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getAmsUser: async(amsUserId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}ams/getAmsUser`,
        {
          params: { amsUserId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getAmsUserPort: async(amsUserId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}ams/getAmsUserPort`,
        {
          params: { amsUserId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getAmsUserScac: async(amsUserId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}ams/getAmsUserScac`,
        {
          params: { amsUserId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getAmsVessel: async(amsUserId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}ams/getAmsVessel`,
        {
          params: { amsUserId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getAmsGroupId: async(scac: string, useEManifest: boolean) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}ams/getAmsGroupId`,
        {
          params: {scac, useEManifest}
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getAmsGroups: async() => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}ams/getAmsGroups`,
        {
          params: {}
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getAmsAccountTypeList: async() => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}ams/getAmsAccountTypeList`,
        {
          params: {}
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },
    
    readStatementData : async(overDue: string, excludeZeroTerm: string, invoiceDateFrom: string, invoiceDateTo: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/readStatementData`,
        { params : {overDue, excludeZeroTerm, invoiceDateFrom, invoiceDateTo}}
      ));
        return apiResult.success ? apiResult.data : null;
    },

    checkAttachment: async ( formData: FormData) => {
      const apiResult = await apiCallResolver(() => apiClient.post(
          `${apiUrl}reportOptionBase/checkattachment`,
           formData 
      ));
      return apiResult.success ? apiResult.data : null;
    },

    sendEMail: async (args: any) => {
      const apiResult = await apiCallResolver(() => apiClient.post(
          `${apiUrl}reportOptionBase/sendEMail`,
          { args }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getCheckCompanyIsUsed: async (comId: number) => {
        const apiResult = await apiCallResolver(() => apiClient.get(
            `${apiUrl}Comp/companyIsUsed`,
            {
                params: comId
            }
        ));
        return apiResult.success ? apiResult.data : null;
    },

    getCheckCompanyIsUsedIl3: async (id: number, il3LicID: string) => {
        const apiResult = await apiCallResolver(() => apiClient.get(
            `${apiUrl}Comp/companyIsUsedIl3`,
            {
                params: { id, il3LicID }
            }
        ));
        return apiResult.success ? apiResult.data : null;
    },

    pimsSaveCustomer: async (data: PimsCustomer[], pimsId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.put(
        `${apiUrl}Comp/pimsSaveCustomer`,
        data,
        {
          params: { pimsId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    subFill: async (_AcctTableName: string, _AdjType: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}FmsModAccount/SubFill`,
        {
          params: { _AcctTableName, _AdjType }
        }
        ));
        return apiResult.success ? apiResult.data : null;
    },
    
    subUpdate: async(_AcctTableName: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}FmsModAccount/SubUpdate`,
        {
          params: { _AcctTableName }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    readYearEndData : async() => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}${JobNames.EndYearProc}/readYearEndData`, {}
      ));
      return apiResult && apiResult.success ? apiResult.data : null;
    },

    checkFYPeriod : async(beginDate: string, endDate: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}${JobNames.EndYearProc}/checkFYPeriod`, {
          params: { beginDate, endDate }
        }
      ));
      return apiResult && apiResult.success ? apiResult.data : null;
    },

    checkRecogStatus : async(endDate: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}${JobNames.EndYearProc}/checkRecogStatus`, {
          params: { endDate }
        }
      ));
      return apiResult && apiResult.success ? apiResult.data : null;
    },

    runYearEnd : async(beginDate: string, endDate: string, earningGLNo: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}${JobNames.EndYearProc}/runYearEnd`, {
          params: { beginDate, endDate, earningGLNo }
        }
      ));
      return apiResult && apiResult.success ? apiResult.data : null;
    },

    runReverse : async(thisFiscalYear: number, nextFiscalYear: number) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}${JobNames.EndYearProc}/runReverse`, {
          params: { thisFiscalYear, nextFiscalYear }
        }
      ));
      return apiResult && apiResult.success ? apiResult.data : null;
    },

    saveFiscalYearHistory : async(action: string, fyNo: string, fiscalYear: number) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}${JobNames.EndYearProc}/saveFiscalYearHistory`, {
          params: { action, fyNo, fiscalYear }
        }
      ));
      return apiResult && apiResult.success ? apiResult.data : null;
    },

    readFYHistory : async() => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}${JobNames.EndYearProc}/readFYHistory`, {
          params: { }
        }
      ));
      return apiResult && apiResult.success ? apiResult.data : null;
    },

    periodClose : async(closeDate: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}${JobNames.EndYearProc}/periodClose`, {
          params: { closeDate }
        }
      ));
      return apiResult && apiResult.success ? apiResult.data : null;
    },

    saveFiscalYearSetting: async(sqlList: SQLInfo[], params:any) => {
      const apiResult = await apiCallResolver(() => apiClient.put(
        `${apiUrl}${JobNames.EndYearProc}/saveFiscalYearSetting`, 
        sqlList,
        { params }
      ));
    
      return apiResult.success ? apiResult.data : null;
    },

    beginJournalCount : async() => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}${JobNames.EndYearProc}/beginJournalCount`, {
          params: { }
        }
      ));
      return apiResult && apiResult.success ? apiResult.data : null;
    },
    
    getEmailRecipient: async() => {
      //const eventSource = new EventSource(`${apiUrl}settings/dbcheck?${urlParams}&jwt=${apiClient.defaults.headers.common.Authorization}`, { withCredentials: true } );
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}report/getEmailRecipient2?jwt=${apiClient.defaults.headers.common.Authorization}`,
        //params: { jwt= apiClient.defaults.headers.common.Authorization }
      ));
    
      return apiResult.success ? apiResult.data : null;
    },

    deletePimsItem: async (jobName: string, data: any) => {
      const apiResult = await apiCallResolver(() => apiClient.delete(
        `${apiUrl}pims/deletePimsItem`,
        {
          params: { jobName, data }
        }
      ));
      return apiResult && apiResult.success ? apiResult.data : null;
    },

    // getLicenseeList: async(username:string, password:string) => {
    //   const apiResult = await apiCallResolver(() => apiClient.get(
    //     `${apiUrl}user/getLicenseeList`,{
    //       params: { email: username, password: password }
    //     }
    //   ));
    //   return apiResult.success ? apiResult.data : null;
    // },

    // loginPortalAdmin: async (licid: string, username: string, password: string) => {
    //   const apiResult = await apiCallResolver(() => apiClient.get(
    //     `${apiUrl}user/loginPortalAdmin/`, {
    //       params: {
    //         licid: licid,
    //         email: username,
    //         password,
    //       },
    //     }
    //   ));

    //   return {
    //     success: apiResult.success,
    //     user: apiResult.data,
    //     errorMessage: !apiResult.success ? 'User not found' : null,
    //   }

    // },

    voidBktf: async (sqlList:SQLInfo[]) => {
      const apiResult = await apiCallResolver(() => apiClient.put(
        `${apiUrl}bktf/savevoid`,
         sqlList,
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getBalance: async(c:any) => {  
      const apiResult = await apiCallResolver(()=> apiClient.get(
        `${apiUrl}bktf/getBalance`,
          { params: c}
      ));
      return apiResult.success ? apiResult.data : null;
    },

    voidAdjustment: async(jobName: JobNames, sqlList:SQLInfo[]) => {
      const apiResult = await apiCallResolver(() => apiClient.put(
        `${apiUrl}${jobName}/savevoid`,
        sqlList,
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getSecControlList: async() => {
      const apiResult = await apiCallResolver(() => apiClient.post(
        `${apiUrl}Secu/getsecuritycontrollist`,
        {}
        ), true, true);
      return apiResult.success ? apiResult.data : null;
    },

    showTaxReturnList: async(category: string, toDate: string, fromDate: string, branch: string, useBranch: boolean) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}strt/showTaxReturnList`,{
          params: {category, toDate, fromDate, branch, useBranch}
        } 
      ));
      return apiResult.success ? apiResult.data : null;
    },

    voidTaxReturn: async(sqlList: SQLInfo[]) => {
      const apiResult = await apiCallResolver(() => apiClient.put(
        `${apiUrl}strt/savevoid`,
        sqlList,
      ));
      return apiResult.success ? apiResult.data : null;
    },  

    selectDraftList: async (date: any) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}paymtp/selectDraftList`,
        {params:{date}}
      ));
    
      return apiResult.success ? apiResult.data : null;
    },

    paymentprepdelete: async(sqlList:SQLInfo[]) => {
      const apiResult = await apiCallResolver(() => apiClient.put(
        `${apiUrl}paymtp/paymentprepdelete`,
        sqlList,
      ));
      return apiResult.success ? apiResult.data : null;
    },
    selectUnPort: async(unCode: string, id: number) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}unlo/selectunport`,
        {params: {unCode, id}}
      ));
      return apiResult.success ? apiResult.data : null;
    },
    checkDuplicatedKeys: async (jobName: string, listItemType:string, insList: any ,chgList:any) => {
      const apiResult = await apiCallResolver(() => apiClient.post(
        `${apiUrl}modlist/checkDuplicatedKeys`,
        {jobName, listItemType, insList, chgList }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    readAEMData: async(tbId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/readAEMData`,
        {
          params:{ tbId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getBankBalanceInfo: async(id: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}bankbalance/getBankBalanceInfo`,
        {
          params:{ id }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getBegDate: async (date: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}fmsmodfunc/getBegDate`,
        { params: { date } }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getAcctStartDate: async () => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}fmsmodfunc/getAcctStartDate`
      ));
      return apiResult.success ? apiResult.data : null;
    },

    selectBankBalList: async (begDate: string, endDate: string, showInactive: boolean, showBadARBank: boolean, priCurr: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}bankbalance/selectBankBalList`,
        {
          params: { begDate, endDate, showInactive, showBadARBank, priCurr }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    readCustomerNameData: async(tbId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/readCustomerNameData`,
        {
          params:{tbId}
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    checkReconDate: async(reconDate: string, bankId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.post(
        `${apiUrl}BRec/checkReconDate`,
        null,
        {
          params:{reconDate, bankId}
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },
    /**
     * @param customer 
     * @returns 
     */
    inventoryCount : async(customer: string,) => {
      const apiResult = await apiCallResolver(() => apiClient.post(
        `${apiUrl}whcus/inventoryCount`,
        {
          params: { customer}
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    //#region - whLoc
    getInventoryCount: async (id: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}whcus/getInventoryCount`,
        { params: { id } }
      ));
      return apiResult.success ? apiResult.data : null;
    },
    //#endregion - whLoc

    checkupHQBranch: async () => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}bran/checkupHQBranch`,
        {
           params: {}          
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },
    //#endregion - whLoc

    trackingCancelRequest: async (jobName: string, jobId: number) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}trackRequest/trackingCancelRequest`,
        {
          params: { jobName, jobId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    trackingUpdateRequest: async (noticeToUpdates: any[]) => {
      const apiResult = await apiCallResolver(() => apiClient.put(
        `${apiUrl}trackRequest/trackingUpdateRequest`,
        noticeToUpdates,
      ));
      return apiResult.success ? apiResult.data : null;
    },

    trackingCheckShippingLine: async (vcScac: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}trackRequest/trackingCheckShippingLine`,
        {
          params: { vcScac }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getTrackRequest: async (jobName: string, jobId: number, trType: string, isNew: boolean) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
      `${apiUrl}trackRequest/getTrackRequest`,
        {
          params: { jobName, jobId, trType, isNew }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    checkThisMonthRequests: async () => {
      const apiResult = await apiCallResolver(() => apiClient.get(
      `${apiUrl}trackRequest/checkThisMonthRequests`,
        {}
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getCompaniesToNotify: async (jobName: string, jobId: number) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
      `${apiUrl}trackRequest/getCompaniesToNotify`,
        {
          params: { jobName, jobId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getCode: async (vcScac: string, depart: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
      `${apiUrl}trackRequest/getCode`,
        {
          params: { vcScac, depart }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    trackingRequestByMbl: async (jobName: string, jobId: number, reqType: string, emails: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}trackRequest/trackingRequestByMbl`,
        {
          params: { jobName, jobId, reqType, emails }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    trackingRequestByContainer: async (containerNo: string, emails: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}trackRequest/trackingRequestByContainer`,
        {
          params: { containerNo, emails }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    trackingLogMblEvents: async (jobName: string, jobId: number, logs: any[]) => {
      const apiResult = await apiCallResolver(() => apiClient.put(
        `${apiUrl}trackRequest/trackingLogMblEvents`, 
        logs,
        {
          params: { jobName, jobId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    trackingLogHblMilestone: async (eventLog: BlEventLog) => {
      const apiResult = await apiCallResolver(() => apiClient.put(
        `${apiUrl}trackRequest/trackingLogHblMilestone`,
        eventLog
      ));
      return apiResult.success ? apiResult.data : null;
    },
    
    chkdupContainer: async (jobId:string, data:any) => {
      const apiResult = await apiCallResolver(() => apiClient.post(
        `${apiUrl}cntr/checkdata`,
        data,
        { params: {jobId} }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    chkBCodeDelete: async (jobId:string, data:any) => {
      const apiResult = await apiCallResolver(() => apiClient.post(
        `${apiUrl}bCode/checkdelete`,
        data,
        { params: {jobId} }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    recreateJournal: (cmd: DbCheckCmd, onMessage: (message: string) => void, onComplete: Function) => {
      const urlParams = Object.entries(cmd).map(e => e.join('=')).join('&');

      const eventSource = new EventSource(`${apiUrl}settings/recreateJournal?${urlParams}&jwt=${apiClient.defaults.headers.common.Authorization}`, { withCredentials: true } );
      eventSource.onmessage = (e) => {
        if (_.startsWith(e.data, '<<end>>')) {
          eventSource.close();
          onComplete(e.data);
          return;
        }
        onMessage(e.data);
      };
    },

    getReceivingItem: async ( data:any) => {
      const apiResult = await apiCallResolver(() => apiClient.post(
        `${apiUrl}whrcv/getReceivingItem`,
        data
      ));
      return apiResult.success ? apiResult.data : null;
    },

    signUpload: async (formData: FormData) => {
      const apiResult = await apiCallResolver(() => apiClient.post(
        `${apiUrl}user/signUpload`,
        formData
      ));
      return apiResult.success ? apiResult.data : null;
    },

    clearSign: async (formData: FormData) => {
      const apiResult = await apiCallResolver(() => apiClient.post(
        `${apiUrl}user/clearSign`, 
        formData
      ));
      return apiResult.success ? apiResult.data : null;
    },

    saveJournalHistoryByTask : async(tbName: string, tbid: string, userID: string, jourType: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}${JobNames.SlipJournal}/saveJournalHistoryByTask`, {
          params: { tbName, tbid, userID, jourType }
        }
      ));
      return apiResult && apiResult.success ? apiResult.data : null;
    },

    getFiscalYear : async(postDate: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}${JobNames.SlipJournal}/getFiscalYear`, {
          params: { postDate }
        }
      ));
      return apiResult && apiResult.success ? apiResult.data : null;
    },

    voidJournal : async(id: string, branch: string, userID: string, jourType: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}${JobNames.SlipJournal}/voidJournal`, {
          params: { id, branch, userID, jourType }
        }
      ));
      return apiResult && apiResult.success ? apiResult.data : null;
    },

    readBadARExpense : async() => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}${JobNames.SlipJournal}/readBadARExpense`, {
          params: { }
        }
      ));
      return apiResult && apiResult.success ? apiResult.data : null;
    },
    
    updateBadARExpense: async (paymentInfo: GeneralValue[]) => {
      const apiResult = await apiCallResolver(() => apiClient.put(
        `${apiUrl}${JobNames.SlipJournal}/updateBadARExpense`,
        paymentInfo
      ));
      return apiResult.success ? apiResult.data : [];
    },

    updateSTBID: async (id: number) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}${JobNames.SlipJournal}/updateSTBID`, {
        params: { id }
      }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    deletePKey: async (skey: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}${JobNames.SlipJournal}/deletePKey`, {
        params: { skey }
      }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    insertPKey: async (skey: string, postDate: string, branch: string, seqNo: string, id: number) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}${JobNames.SlipJournal}/insertPKey`, {
        params: { skey, postDate, branch, seqNo, id }
      }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    updateHeadTbId: async (hdId: number) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}${JobNames.SlipJournal}/updateHeadTbId`, {
        params: { id: hdId }
      }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    selectSeq: async (postDate: string, key: string, branch: string, seq: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}${JobNames.SlipJournal}/selectSeq`, {
        params: { postDate, key, branch, seq }
      }
      ));
      return apiResult && apiResult.success ? apiResult.data : null;
    },

    getSearchJournalQuery: async(selectedIndex: number, jobName: string, searchText: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}${JobNames.SlipJournal}/getSearchJournalQuery`, {
          params: { selectedIndex, jobName, searchText }
        }
      ));
      return apiResult && apiResult.success ? apiResult.data : null;
    },

    selectFormParaId: async (jourType: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}${JobNames.SlipJournal}/selectFormParaId`, {
        params: { jourType }
      }
      ));
      return apiResult && apiResult.success ? apiResult.data : null;
    },

    saveMinutes: async(enterMin: string, userId: string) => {
      await apiCallResolver(() => apiClient.post(
        `${apiUrl}reminder/saveMinutes`,
        { enterMin, userId }
      ));
    },

    getReminderTaskTime: async (id: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}fmsmodfunc/getReminderTaskTime`,
        { params: { id } }
      ));
      return apiResult.success ? apiResult.data : null;
    },
    
    exportToExcel: async (args: any) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}report/exportreport`,
        {
          params: { args, jwt:apiClient.defaults.headers.common.Authorization}
        }
      ));  
      return apiResult.success ? apiResult.data : null;
    },

    getBLHDInfo: async (hdOpType: any, hdTBId: number, opAcctIds: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}Invo/getBLHDInfo`,
        {params: {hdOpType, hdTBId, opAcctIds}}
      ));
      return apiResult.success ? apiResult.data: null;
    },

    getAIMInfo: async(tbId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/readIRC225Data`,
        {
          params:{ tbId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getAemMain: async (id: number) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}AeHb/getAemMain`,
        {
          params: { id }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getDefaultCharge: async (aehOtherCharge: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}AeHb/getDefaultCharge`,
        {
          params: { aehOtherCharge }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getCarrierRate: async (carrierId: number) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}AeHb/getCarrierRate`,
        {
          params: { carrierId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    setOblCol: async (id: number, ret: string, now: string) => {
      const apiResult = await apiCallResolver(() => apiClient.post(
        `${apiUrl}oihb/setoblcol`,
        { id, ret, now }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getIsfno: async (amsblno: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}oihb/getisfno`,
        {
          params: { amsblno: amsblno }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getExistedAmsIsfNo: async (amsblno: string, isfNo: string, amsIds: string, isfIds: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}oihb/getExistedAmsIsfNo`,
        {
          params: { amsblno, isfNo, amsIds, isfIds }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    checkReleaseOrder: async (id: number) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}fmsmodedi/checkReleaseOrder`,
        {
          params: { itemId: id }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },
    
    getReleaseHblInfo: async (hblId: number, billToId: number) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}oihb/getReleaseHblInfo`,
        {
          params: { hblId, billToId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    saveReleaseHblInfo: async (sqlList: SQLInfo[], params: any) => {
      const apiResult = await apiCallResolver(() => apiClient.put(
        `${apiUrl}oihb/saveReleaseHblInfo`,
        sqlList,
        { params }
      ));

      return apiResult.success ? apiResult.data : null;
    },

    getDataForRelease: async (hblId: number) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}oihb/getDataForRelease`,
        { params: { hblId } }
      ));

      return apiResult.success ? apiResult.data : null;
    },

    makeCfsRelease: async (params: any) => {
      const apiResult = await apiCallResolver(() => apiClient.put(
        `${apiUrl}fmsmodedi/makecfsrelease`,
        {
          id: params.id,
          contact: params.contact,
          requestDept: params.requestDept
        }
      ));

      return apiResult.success ? apiResult.data : null;
    },

    releaseNow: async (params: any) => {
      const apiResult = await apiCallResolver(() => apiClient.put(
        `${apiUrl}oihb/ReleaseNow`,
        null,
        { params }
      ));

      return apiResult.success ? apiResult.data : null;
    },
    
    getEDI310: async (invoId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}e310/detail`,
        {
          params: { invoId}
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getEDI310Batch: async (invoId: string, startDate:string, endDate : string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}e310/detailBatch`,
        {
          params: { invoId, startDate, endDate}
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    make310: async (entity : EdiSharedInfo, jobtype: string, invoHdIds: string, tempDir: string, ftpScac: string, ediSeqNo: string) => {
      const apiResult = await apiCallResolver(() => apiClient.put(
        `${apiUrl}e310/make310`,
        entity,        { 
          params:{
            jobtype: jobtype,
            invoHdIds : invoHdIds,
            tempDir : tempDir,
            ftpScac : ftpScac,
            ediSeqNo : ediSeqNo
          } 
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getAcctInfo: async (jobname: string, id:string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}${jobname}/getacctinfo`,
        {
          params: {id}
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    readAcctDetailData: async (tbname: string, tbid:string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}acct/detail`,
        {
          params: { tbname, tbid}
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    acctupdateStatusOP: async(sqlList:SQLInfo) => {
      const apiResult = await apiCallResolver(() => apiClient.post(
        `${apiUrl}acct/updateStatusOP`,
        sqlList
      ));
      return apiResult.success ? apiResult.data : null;
    },
    
    checkFileClosed: async (jobName: string, jobId:string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}fmsmodfunc/checkFileClosed`,
        {
          params: {jobName, jobId}
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },
    checkUsePayroll: async () => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}ssetu/checkUsePayroll`,
      ));
      return apiResult.success ? apiResult.data : null;
    },
    getDefaultPayrollCode: async () => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}ssetu/getDefaultPayrollCode`,
      ));
      return apiResult.success ? apiResult.data : null;
    },
    getPayrollGroupSummary: async (payGroupId: number, payrollId: number) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}PAYROLL2/getPayrollGroupSummary`,
        {
          params: { payGroupId, payrollId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    active2FA: async (username:string, password:string, code:string) => {
      const apiResult = await apiCallResolver(() => apiClient.post(
        `${apiUrl}user/active2fa`,
        {
          username, password, code
        },
      ));

      
      if (apiResult.success) {
        return apiResult.success ? apiResult.data : null;
      } else {
        return ({
          success: false,
          result: 'Please refresh the page and try again.'
        });
      }
    },
    
    suntekAdminAuth: async (username:string, password:string, code:string) => {
      const apiResult = await apiCallResolver(() => apiClient.post(
        `${apiUrl}user/suntekAdminAuth`,
        {
          username, password, code
        },
      ));

      if (apiResult.success) {
        return apiResult.success ? apiResult.data : null;
      } else {
        return ({
          success: false,
          result: 'Please refresh the page and try again.'
        });
      }
    },

    readLabelFolderList: async(tbName: string, tbId: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}reportOptionBase/readLabelFolderList`,
        {
          params:{ tbName, tbId }
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },
    getSearchAllTableFromType: async (searchType: string, searchText:string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}modList/getSearchAllTableFromType`,
        {
          params: {searchType, searchText}
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },
    getSearchAllTable: async (searchText:string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}modList/getSearchAllTable`,
        {
          params: {searchText}
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },
    saveSQLInfoList: async (sqlList: SQLInfo[]) => {
      const apiResult = await apiCallResolver(() => apiClient.put(
        `${apiUrl}oihb/saveSQLInfoList`,
        sqlList,
      ));
    
      return apiResult.success ? apiResult.data : null;
    },
    setCostSalesCode: async (selectCode:string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}bcode/setCostSalesCode`,
        {
          params: {selectCode}
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getDownloadList: async (jobName: JobNames, id: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}download/getDownloadList`,
        { params: { jobName, id }}
      ));
      
      return apiResult.success ? apiResult.data : null;
    },

    download: async (model: DisplayDownload, jobName: JobNames, id: string) => {
      const apiResult = await apiCallResolver(() => apiClient.post(
        `${apiUrl}download/download`,
        {model, jobName, id}
      ));
      
      return apiResult.success ? apiResult.data : null;
    },

    getManifestProducts: async (amsId1: string, amsId2: string, amsId3: string, useeManifest: boolean) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}ams/getManifestProducts`,
        { params: { amsId1, amsId2, amsId3, useeManifest } }
      ), true, true);

      return apiResult.success ? apiResult.data : null;
    },
    getIssuerInfo: async () =>{
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}btDo/getIssuerInfo`
      ));
      return apiResult.success ?apiResult.data: null;
    },
    getInbondLink: async () =>{
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}utility/getInbondLink`
      ));
      return apiResult.success ?apiResult.data: null;
    },
    getSupportLink: async () =>{
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}utility/getSupportLink`
      ));
      return apiResult.success ?apiResult.data: null;
    },
    
    block: async (jobName: JobNames, id: number, isBlock: boolean) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}download/block`,
        { params: { jobName, id, isBlock }}
      ));
      
      return apiResult.success ? apiResult.data : null;
    },
     chkBCodeDup: async (jobId:string, codeName:string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}bCode/checkDuplicate`,
        {
          params: { jobId, codeName}
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },
    actionNotIssuedBillings: async (actionType:string) => {
      const apiResult = await apiCallResolver(() => apiClient.put(
        `${apiUrl}system/actionNotIssuedBillings`,
        null,
        {
          params: actionType
        }
      ));
    
      return apiResult.success ? apiResult.data : null;
    },

    checkNonExistAcctCode: async (glType:string, glGroup:string, code: string) => {
      const apiResult = await apiCallResolver(() => apiClient.post(
        `${apiUrl}glno/checkNonExistAcctCode`,
        {
          glType, glGroup, code
        }
      ));
      return apiResult.success ? apiResult.data : null;
    },

    getAesLink: async () =>{
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}utility/getAesLink`
      ));
      return apiResult.success ?apiResult.data: null;
    },

    getAmsLink: async () => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}utility/getAmsLink`
      ));
      return apiResult.success ? apiResult.data: null;
    },

    getIsfLink: async (url: string) => {
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}utility/getIsfLink`,
        {
          params: { url }
        }
      ));
      return apiResult.success ? apiResult.data: null;
    },

    getSharedUrl: async (trackingNo: string) =>{
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}tive/getSharedUrl?trackingNo=${trackingNo}`
      ));
      return apiResult.success ? apiResult.data: null;
    },

    getRtdShipInfo: async (trackingNo: string) =>{
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}tive/getRtdShipInfo?trackingNo=${trackingNo}`
      ));
      return apiResult.success ? apiResult.data: null;
    },

    getKeyFromBillings: async (ids: string) =>{
      const apiResult = await apiCallResolver(() => apiClient.get(
        `${apiUrl}btLp/getKeyFromBillings?ids=${ids}`
      ));
      return apiResult.success ? apiResult.data: null;
    },
    setSaveUseIsf: async (id: number, useIsf: number) =>{
      const apiResult = await apiCallResolver(() => apiClient.put(
        `${apiUrl}btOdr/setSaveUseIsf`,
        null,
        { params: { id: id, useIsf: useIsf}}
      ));
      return apiResult.success ? apiResult.data: null;
    },
  }
}

export default createIfmsApi;
